import React from 'react'
import { RecipesStatistics } from '@/api/types'
import HorizontalPlot from './HorizontalPlot'
import { useTranslation } from 'react-i18next'

import './RecipesStats.css'
import Histogram from './Histogram'
import HistogramMacroDistribution from './HistogramMacroDistribution'
import MacroKcalPie, { aggregateMacros } from '@/features/MacroKcalPie/MacroKcalPie'

type Props = {
  statistics: RecipesStatistics
}

export default function RecipeStats({ statistics }: Props) {
  const { t } = useTranslation()
  return (
    <div className="dashboard-grid">
      <div className="total-recipes">
        <div>{t('recipe.totalRecipes')}</div>
        <h2>{statistics.totalRecipes}</h2>
      </div>
      <div className="meal-types">
        <div>{t('recipe.mealTypes')}</div>
        <HorizontalPlot
          data={statistics.mealTypes}
          tickFormat={(d) => t(`recipe.mealTypeOptions.${d}` as any)}
          labelsFontSize="0.8rem"
        />
      </div>
      <div className="diets-suitable-recipes">
        <div>{t('recipe.dietStats')}</div>
        <HorizontalPlot
          data={statistics.suitableForDietsStats}
          options={{
            height: 300,
            marginLeft: 180,
          }}
          tickFormat={(d) => t(`recipe.dietOptions.${d}` as any)}
          labelsFontSize="1.1rem"
        />
      </div>
      <div className="allergen-suitable-recipes">
        <div>{t('recipe.allergenStats')}</div>
        <HorizontalPlot
          data={statistics.suitableForAllergensStats}
          options={{
            height: 480,
            marginLeft: 120,
          }}
          tickFormat={(d) => t(`recipe.allegenOptions.${d}` as any)}
          labelsFontSize="1.1rem"
        />
      </div>
      <div className="cooking-time">
        <div>Total Cooking Time</div>
        <HorizontalPlot
          data={statistics.totalTimes}
          options={{ height: 170 }}
          labelsFontSize="1.1rem"
        />
      </div>
      <div className="expertise-levels">
        <div>{t('recipe.expertise')}</div>
        <HorizontalPlot
          data={statistics.expertiseLevels}
          options={{
            marginLeft: 120,
            height: 150,
            y: {
              label: null,
              domain: statistics.expertiseLevels.map((d) => d.name),
            },
          }}
          tickFormat={(d) => t(`recipe.expertiseOptions.${d}` as any)}
          labelsFontSize="1.1rem"
        />
      </div>
      <div className="energy-distribution">
        <div>{t('recipe.energyDistribution.title')}</div>
        <Histogram
          data={statistics.energyPerServingValues}
          options={{
            x: {
              label: t('recipe.energyDistribution.xAxisLabel'),
            },
            y: {
              label: t('recipe.energyDistribution.yAxisLabel'),
            },
          }}
        />
      </div>
      <div className="servings">
        <div>{t('recipe.servingsStats')}</div>
        <HorizontalPlot
          data={statistics.servingsStats}
          options={{
            height: 300,
            y: {
              label: null,
              domain: statistics.servingsStats
                .sort((a, b) => parseInt(a.name) - parseInt(b.name))
                .map((d) => d.name),
            },
          }}
          labelsFontSize="1.1rem"
        />
      </div>
      <div className="seasons">
        <div>{t('recipe.seasons')}</div>
        <HorizontalPlot
          data={statistics.seasonsStats}
          tickFormat={(d) => t(`recipe.seasonOptions.${d}` as any)}
          options={{
            height: 300,
          }}
          labelsFontSize="0.8rem"
        />
      </div>
      <div className="macro-distribution">
        <div>{t('recipe.macroDistributionStats')}</div>
        <HistogramMacroDistribution
          data={statistics.recipeMacroDistributions}
          options={{
            height: 500,
          }}
        />
      </div>
      <div className="macro-distribution-pie">
        <div style={{ marginBottom: '20px' }}>{t('recipe.macroDistributionAggStats')}</div>
        <MacroKcalPie
          data={aggregateMacros(statistics.recipeMacroDistributions)}
          options={{
            labelRadius: 100,
            fontSize: '0.7rem',
            height: 330,
            width: 330,
          }}
        />
      </div>
    </div>
  )
}
